import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  ChevronRight, 
  ChevronDown,
  Building2, 
  Shield, 
  AlertCircle, 
  CheckCircle,
  Users,
  CreditCard,
  Database,
  Lock,
  Scale,
  Clock,
  Headphones,
  FileCheck,
  Ban,
  Gavel,
  Mail
} from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const TermsOfService = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, type: 'spring', stiffness: 100 } }
  };

  // Subtle tech grid
  const TechGrid = () => (
    <div className="absolute inset-0 z-0">
      <div className="absolute w-full h-full grid grid-cols-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-r border-gray-300 h-full" />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-12 gap-px opacity-5">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="border-b border-gray-300 w-full" />
        ))}
      </div>
    </div>
  );

  // Soft scanline
  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-400/10 to-transparent h-32"
      animate={{ top: ['-10%', '110%'] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  );

  const lastUpdated = "March 2, 2026";

  const keyTerms = [
    {
      icon: Building2,
      title: "Platform License",
      summary: "Non-exclusive license for authorized clients and organizations"
    },
    {
      icon: Database,
      title: "Your Data",
      summary: "You retain full ownership of all your data"
    },
    {
      icon: Clock,
      title: "Service Availability",
      summary: "Defined in individual customer agreements"
    },
    {
      icon: Shield,
      title: "Security",
      summary: "Security practices we're building toward"
    }
  ];

  const sections = [
    {
      id: "license",
      icon: FileCheck,
      title: "License & Access",
      summary: "Your rights to use our products and services",
      content: [
        "Non-exclusive, non-transferable license during your active subscription",
        "For authorized organizations and their designated users only",
        "Includes all features available in your subscription tier",
        "You are responsible for managing your own Authorized Users",
        "No sublicensing, reverse engineering, or unauthorized access permitted"
      ]
    },
    {
      id: "subscription",
      icon: CreditCard,
      title: "Subscription & Fees",
      summary: "Pricing and payment terms",
      content: [
        "Subscription tiers vary by product—see your service agreement for details",
        "Annual billing, due within 30 days of invoice",
        "Fee adjustments with 60 days notice, max 5% annual increase",
        "Auto-renewal unless 60 days written notice given prior to term end",
        "Client responsible for all applicable taxes and duties"
      ]
    },
    {
      id: "data",
      icon: Database,
      title: "Data Ownership",
      summary: "Your data belongs to you",
      content: [
        "You retain all rights, title, and interest in your data",
        "We access your data solely to provide and improve the service",
        "Export your data at any time in standard formats (CSV, JSON, XML, PDF)",
        "30-day data retrieval period after termination of service",
        "Daily encrypted backups with 90-day retention"
      ]
    },
    {
      id: "security",
      icon: Lock,
      title: "Security & Privacy",
      summary: "How we protect your information",
      content: [
        "Security practices are being built out as we grow — see our Security page for details",
        "All data handling governed by our Privacy Policy"
      ]
    },
    {
      id: "acceptable-use",
      icon: CheckCircle,
      title: "Acceptable Use",
      summary: "Guidelines for using our services",
      content: [
        "Use the platform for its intended organizational and operational purposes",
        "Keep all data confidential and secure per your obligations",
        "Do not store unlawful, harmful, or infringing content",
        "Do not attempt to access other clients' data or systems",
        "Comply with all applicable laws, regulations, and contractual obligations"
      ]
    },
    {
      id: "prohibited",
      icon: Ban,
      title: "Prohibited Uses",
      summary: "Activities that will result in termination",
      content: [
        "Commercial exploitation or resale of platform data without authorization",
        "Unauthorized sharing of data outside your organization",
        "Circumventing or attempting to bypass security measures",
        "Using the service for purposes not authorized under your agreement",
        "Any illegal or fraudulent activities"
      ]
    },
    {
      id: "sla",
      icon: Headphones,
      title: "Service Levels",
      summary: "Our commitments to you",
      content: [
        "Service availability targets will be defined in individual customer agreements",
        "Support response times will be defined in individual customer agreements"
      ]
    },
    {
      id: "ip",
      icon: Shield,
      title: "Intellectual Property",
      summary: "Ownership of platform and innovations",
      content: [
        "Reticuli Technologies retains all rights to its platforms, software, and proprietary technology",
        "Client feedback and suggestions may be used to improve services without obligation",
        "Custom development work ownership defined in individual service agreements",
        "All trademarks, logos, and brand assets remain the property of their respective owners",
        "Neither party gains rights to the other's pre-existing intellectual property"
      ]
    },
    {
      id: "liability",
      icon: Scale,
      title: "Liability & Disputes",
      summary: "Legal limitations and resolution",
      content: [
        "Total liability capped at 12 months of fees paid under the applicable agreement",
        "Neither party liable for consequential, incidental, or punitive damages",
        "Disputes resolved through binding arbitration unless otherwise agreed",
        "Mississippi law governs this agreement",
        "30-day informal resolution period required before formal proceedings"
      ]
    },
    {
      id: "termination",
      icon: Gavel,
      title: "Termination",
      summary: "How this agreement can end",
      content: [
        "Either party may terminate for material breach with a 30-day cure period",
        "Reticuli Technologies may terminate immediately for prohibited use violations",
        "60 days written notice required for non-renewal",
        "30-day data export window provided after termination",
        "Prorated refund only if Reticuli Technologies is in material breach"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Hero Section */}
      <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden bg-black">
        <ParticleBackground />
        <TechGrid />
        <ScanLine />

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-700/30 to-transparent" />

        <div className="container mx-auto px-4 z-20 relative pt-32 pb-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-gray-700/50 bg-gray-900/50">
                <FileText className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
              </div>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-extralight tracking-wider text-white mb-3"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Terms of Service
            </motion.h1>

            <motion.p variants={itemVariants} className="text-gray-400 tracking-widest text-sm mb-4">
              RETICULI TECHNOLOGIES
            </motion.p>

            <motion.p variants={itemVariants} className="text-gray-500 text-sm">
              Last Updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Key Terms Visual Cards */}
      <section className="relative py-12 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {keyTerms.map((term, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-gray-900/60 border border-gray-700/30 rounded-lg p-5 text-center hover:border-gray-600/50 transition-colors duration-300"
                >
                  <term.icon className="w-8 h-8 mx-auto mb-3 text-gray-400" strokeWidth={1.5} />
                  <h3 className="text-sm font-medium text-white mb-1">{term.title}</h3>
                  <p className="text-xs text-gray-500">{term.summary}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice Banner */}
      <section className="relative py-6 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 bg-gray-900/40 border border-gray-700/30 rounded-lg px-5 py-4"
            >
              <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <p className="text-sm text-gray-400">
                <span className="text-white font-medium">By using any Reticuli Technologies product or service, you agree to these terms.</span>{' '}
                This agreement includes binding arbitration. These terms apply to all platforms and services operated by Reticuli Technologies, LLC.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Accordion Sections */}
      <section className="relative py-12 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-3">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <button
                  onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  className={`w-full flex items-center justify-between p-5 bg-gray-900/40 border rounded-lg transition-all duration-300 ${
                    expandedSection === section.id 
                      ? 'border-gray-600/50 bg-gray-900/60' 
                      : 'border-gray-800/50 hover:border-gray-700/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      expandedSection === section.id ? 'bg-gray-700/50' : 'bg-gray-800/50'
                    }`}>
                      <section.icon className={`w-5 h-5 ${
                        expandedSection === section.id ? 'text-white' : 'text-gray-500'
                      }`} strokeWidth={1.5} />
                    </div>
                    <div className="text-left">
                      <h3 className="text-base font-medium text-white">{section.title}</h3>
                      <p className="text-xs text-gray-500">{section.summary}</p>
                    </div>
                  </div>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                      expandedSection === section.id ? 'rotate-180 text-white' : ''
                    }`} 
                  />
                </button>
                
                <motion.div
                  initial={false}
                  animate={{ 
                    height: expandedSection === section.id ? 'auto' : 0,
                    opacity: expandedSection === section.id ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="p-5 pt-3 space-y-2">
                    {section.content.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-400">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Summary: What You Get vs What We Expect */}
      <section className="relative py-16 bg-gradient-to-b from-gray-950 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-light text-white text-center mb-10 tracking-wider"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              The Agreement in Brief
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* What You Get */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gray-900/40 border border-gray-700/30 rounded-lg p-6"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gray-800/50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white">What You Get</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Full platform access per your service agreement",
                    "Authorized user accounts for your organization",
                    "Complete data ownership and portability",
                    "Service availability targets defined in your customer agreement",
                    "Dedicated support channels",
                    "Regular feature updates and improvements"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* What We Expect */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gray-900/40 border border-gray-700/30 rounded-lg p-6"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gray-800/50 flex items-center justify-center">
                    <Users className="w-5 h-5 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white">What We Expect</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Use for authorized and lawful purposes only",
                    "Timely payment of subscription fees",
                    "Protect data appropriately per your obligations",
                    "Maintain accurate account and organizational records",
                    "Comply with all applicable laws and regulations",
                    "Report security concerns promptly",
                    "Respect the platform and other users"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-16 border-t border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-2xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Mail className="w-10 h-10 text-gray-600 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-light text-white mb-2">Questions?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Contact us at <span className="text-gray-300">legal@reticulitech.com</span> for terms inquiries
              or <span className="text-gray-300">support@reticulitech.com</span> for platform help.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-600 hover:to-gray-800 text-white font-medium tracking-wider rounded-lg transition-all duration-300 group text-sm border border-gray-600/30"
              >
                Contact Us
                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={16} />
              </Link>
              <Link
                to="/privacy"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-700 hover:border-gray-600 text-gray-400 hover:text-white font-medium tracking-wider rounded-lg transition-all duration-300 text-sm"
              >
                Privacy Policy
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;